import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ToastService } from './toasts.service';

type ApiMethod = 'GET' | 'POST' | 'DELETE';

/**
 * Shared HTTP plumbing for the *Api services. Wraps HttpClient with a
 * uniform error path that surfaces a toast and re-throws the HttpErrorResponse
 * so callers can chain their own catchError for fallback values.
 *
 * Subclasses inject HttpClient and ToastService and forward them through
 * super(...). The original three near-identical apiRequest implementations
 * (PlanogramApiService, FolderApiService, ShareService) collapse here.
 */
export abstract class BaseApiService {
  protected readonly apiUrl = environment.apiUrl;

  constructor(
    protected readonly http: HttpClient,
    protected readonly toastService: ToastService,
  ) {}

  protected handleError(error: HttpErrorResponse, errorMessage: string) {
    console.error('API Error:', error);
    this.toastService.show(errorMessage, {
      classname: `bg-danger text-light p-3 fs-5`,
      delay: 5000,
    });
    return throwError(() => error);
  }

  protected apiRequest<T>(
    endpoint: string,
    method: ApiMethod = 'GET',
    body?: unknown,
    errorMessage: string = 'Operation failed',
  ): Observable<T> {
    endpoint = endpoint.replace(/^\/+/, '');
    const url = `${this.apiUrl}/${endpoint}`;

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      }),
      withCredentials: true,
      observe: 'body' as const,
    };

    let request: Observable<T>;
    if (method === 'POST') {
      request = this.http.post<T>(url, body, options);
    } else if (method === 'DELETE') {
      request = this.http.delete<T>(url, options);
    } else {
      request = this.http.get<T>(url, options);
    }

    return request.pipe(
      catchError((error) => {
        console.error(`Error in ${method} request to ${url}:`, error);
        return this.handleError(error, errorMessage);
      }),
    );
  }
}
